import axios from '@/axios/chatbot'
import { logErrorToDatadog, logWarningToDatadog, windowRef } from '@/common/utils'
import { AxiosError } from 'axios'
import { initFullView } from '@/helpers/fullview'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { registerStorage, unregisterStorage } from './storage'
import { registerWebchat, unregisterWebchat } from './webchat'
import { progressReset } from './progress'
import type { StorageState } from './storage'
import type { WebchatState } from './webchat'
import type { ThunkDispatch } from '../thunk'

export interface UserRecord {
  id?: string
  applicationId?: string
  customerId?: string
  email?: string
}

interface User {
  id: string
  applicationId: string
  customerId: string
}

export interface TokensData {
  api: {
    token: string
  }
  blob: {
    container: string
    uri: string
    sas: string
  }
  directLine: {
    conversationId: string
    token: string
    expires_in: number
    user: string
  }
  user: User
}

export interface UserState {
  tokenGenerationStatus: number
  user: UserRecord
  tokenVerificationStatus: number
  isAuthenticated: boolean
  tempToken?: string
  tempRedirect?: string
  jwtToken?: string
}

export type AuthRootState = {
  user: UserState
  webchat: WebchatState
  storage: StorageState
}

const initialState: UserState = {
  user: {},
  tokenGenerationStatus: 0,
  tokenVerificationStatus: 0,
  isAuthenticated: false,
  tempToken: undefined,
  tempRedirect: undefined,
  jwtToken: undefined,
}

const linkSubjectDetailsToHotJar = ({
  subjectId,
  applicationId,
  customerId,
}: {
  subjectId: string
  applicationId: string
  customerId: string
}) => {
  if (windowRef.hj) {
    windowRef.hj('identify', subjectId, {
      customerId,
      applicationId,
    })
  }
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    email(state, action: PayloadAction<string>) {
      state.user = { ...state.user, email: action.payload }
    },
    tokenGenerationStatus(state, action: PayloadAction<number>) {
      state.tokenGenerationStatus = action.payload
    },
    tokenVerificationStatus(state, action: PayloadAction<number>) {
      state.tokenVerificationStatus = action.payload
    },
    isAuthenticated(state, action: PayloadAction<boolean>) {
      state.isAuthenticated = action.payload
    },
    tempToken(state, action: PayloadAction<string | undefined>) {
      state.tempToken = action.payload
    },
    jwtToken(state, action: PayloadAction<string | undefined>) {
      state.jwtToken = action.payload
    },
    tempRedirect(state, action: PayloadAction<string | undefined>) {
      state.tempRedirect = action.payload
    },
    user(state, action: PayloadAction<User | UserRecord>) {
      state.user = action.payload
    },
  },
})

export const {
  email,
  tokenGenerationStatus,
  tokenVerificationStatus,
  isAuthenticated,
  tempToken,
  jwtToken,
  tempRedirect,
  user,
} = userSlice.actions

export const selectIsAuthenticated = (state: AuthRootState) => {
  const webchat = state.webchat
  const storage = state.storage
  return (
    state.user.isAuthenticated &&
    webchat.conversation !== undefined &&
    webchat.userId !== undefined &&
    webchat.token !== undefined &&
    storage.uri !== undefined &&
    storage.container !== undefined &&
    storage.sas !== undefined
  )
}

export interface GenerateTokenPayload {
  email: string
  redirect: string | (string | null)[]
}

export const generateToken =
  ({ email: userEmail, redirect }: GenerateTokenPayload) =>
  async (dispatch: ThunkDispatch) => {
    dispatch(unregisterWebchat())
    dispatch(unregisterStorage())
    dispatch(progressReset())
    dispatch(tokenGenerationStatus(10))
    try {
      const response = await axios().post('/tokens/generate', {
        email: userEmail,
        redirect: redirect,
      })

      dispatch(tempToken(response.data.token))
      dispatch(tempRedirect(response.data.redirect))
      dispatch(email(userEmail))
    } catch (err) {
      logErrorToDatadog('Generate token error', { errorBody: err as Error })
      dispatch(tokenGenerationStatus(401))
      return
    }
    dispatch(tokenGenerationStatus(200))
  }

export const expireToken = () => async (dispatch: ThunkDispatch) => {
  dispatch(unregisterWebchat())
  dispatch(unregisterStorage())
  dispatch(progressReset())
  await axios().get('/tokens/expire')
  dispatch(isAuthenticated(false))
  dispatch(user({}))
}

export const verifyToken = (token?: string) => async (dispatch: ThunkDispatch) => {
  let response = undefined
  dispatch(jwtToken(token))

  try {
    response = await axios(token).get(`/tokens/verify`)

    const { id: subjectId, customerId, applicationId } = response.data.user

    linkSubjectDetailsToHotJar({ applicationId, customerId, subjectId })

    dispatch(tempToken(undefined))
    dispatch(setTokens(response.data))
  } catch (err) {
    const error = err as AxiosError | Error
    if ('response' in error) {
      logErrorToDatadog('Failed to verify token', { errorBody: error.response })
      response = error.response
      return response
    } else {
      logErrorToDatadog('Unexpected error trying to verify token', {
        errorBody: error,
      })
    }
  }
}

export const setTokens = (tokens: TokensData) => (dispatch: ThunkDispatch) => {
  dispatch(registerStorage(tokens.blob))
  dispatch(
    registerWebchat({
      directLine: tokens.directLine,
      api: tokens.api,
      user: tokens.user,
    }),
  )

  initFullView(tokens.api.token, tokens.user)

  dispatch(isAuthenticated(true))
}

export const tryAuthenticate =
  () => async (dispatch: ThunkDispatch, getState: () => AuthRootState) => {
    if (!selectIsAuthenticated(getState())) {
      try {
        const response = await axios().get('/tokens')
        dispatch(setTokens(response.data))
      } catch (err) {
        /** 401 throws an exception. Just catch it to allow the application to continue. */
        // eslint-disable-next-line no-console
        console.warn('TOKEN_FROM_COOKIE_INVALID ' + err)
        logWarningToDatadog('Invalid token from cookie', { errorBody: err as Error })
      }
    }
  }

export default userSlice.reducer
